import { CommunicationsSetupStatus } from '../onboarding/CommunicationsSetupStatus'
import { useOrganizationAdmin } from '../onboarding/useOrganizationAdmin'
import { CircleAlert, MessageSquareOff, RefreshCw } from 'lucide-react'
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../auth/AuthProvider'
import {
  createContactDirectoryRepository,
  type ContactDirectoryRepository,
} from '../conversations/contact-directory-repository'
import {
  createInteractionOperationalRepository,
  type InteractionOperationalRepository,
} from '../conversations/interaction-operational-repository'
import { useTenant } from '../tenant/TenantProvider'
import { AddPatientDialog } from './components/AddPatientDialog'
import { PatientList } from './components/PatientList'
import { PatientSmsDialog } from './components/PatientSmsDialog'
import styles from './PatientsPage.module.css'
import {
  type PatientAddOutcome,
  type PatientListItem,
  type ServerPatientFilter,
} from './patients-model'
import {
  createPatientsRepository,
  type PatientsRepository,
} from './patients-repository'
import { usePatientOperationalStates } from './use-patient-operational-states'
import { usePatients } from './use-patients'
import {
  createReadSideRepository,
  patientDirectoryMessage,
  type ReadSideStatus,
} from '../read-side/read-side-status'
import { useReadSideStatus } from '../read-side/use-read-side-status'

export function PatientsContent({
  tenantId,
  repository,
  operationalRepository,
  contactDirectoryRepository,
  onOpenPatient,
  onOpenPatientId,
  readSideStatus,
  communicationsSetup,
}: {
  tenantId: string
  communicationsSetup?: ReactNode
  repository: PatientsRepository
  readSideStatus?: ReadSideStatus
  operationalRepository?: InteractionOperationalRepository
  contactDirectoryRepository?: ContactDirectoryRepository
  onOpenPatient?: (patient: PatientListItem) => void
  onOpenPatientId?: (
    patientAuthorityId: string,
    addOutcome?: PatientAddOutcome
  ) => void
}) {
  const [searchQuery, setSearchQuery] = useState('')
  const [activeQuery, setActiveQuery] = useState('')
  const [filter, setFilter] = useState<ServerPatientFilter>('all')
  const [cursor, setCursor] = useState<string | null>(null)
  const [previousCursors, setPreviousCursors] = useState<Array<string | null>>(
    []
  )
  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setActiveQuery(searchQuery.trim())
      setCursor(null)
      setPreviousCursors([])
    }, 300)
    return () => window.clearTimeout(timeout)
  }, [searchQuery])
  const result = usePatients(repository, tenantId, {
    query: activeQuery,
    filter,
    cursor,
  })
  const directoryMessage = readSideStatus
    ? patientDirectoryMessage(readSideStatus)
    : null
  const directoryUpdatedAt = readSideStatus?.patientDirectory.updatedAtMs
  const refreshPatients = result.refresh
  const lastDirectoryRevision = useRef<{ tenantId: string; revision: number }>()
  useEffect(() => {
    const previousRevision =
      lastDirectoryRevision.current?.tenantId === tenantId
        ? lastDirectoryRevision.current.revision
        : undefined
    if (!directoryUpdatedAt || previousRevision === directoryUpdatedAt) return
    lastDirectoryRevision.current = { tenantId, revision: directoryUpdatedAt }
    if (
      previousRevision !== undefined ||
      directoryUpdatedAt > result.dataUpdatedAt
    ) {
      refreshPatients()
    }
  }, [directoryUpdatedAt, refreshPatients, result.dataUpdatedAt, tenantId])
  const operational = usePatientOperationalStates(
    operationalRepository,
    tenantId,
    result.patients
  )
  const [addOpen, setAddOpen] = useState(false)
  const [smsPatient, setSmsPatient] = useState<PatientListItem | null>(null)

  useEffect(() => {
    setSmsPatient(null)
  }, [tenantId])

  const dialogOpen =
    addOpen || Boolean(smsPatient && contactDirectoryRepository)

  return (
    <div className={styles.page}>
      <div
        className={styles.pageContent}
        {...((dialogOpen ? { inert: '' } : {}) as { inert?: string })}
        aria-hidden={dialogOpen || undefined}
      >
        {directoryMessage ? (
          <p className={styles.notice} data-tone="info" role="status">
            <RefreshCw size={15} aria-hidden="true" />
            <span>{directoryMessage}</span>
          </p>
        ) : null}
        {result.refreshFailed ? (
          <p className={styles.notice} data-tone="warning" role="status">
            <CircleAlert size={15} aria-hidden="true" />
            <span>
              Patients could not be refreshed. Showing the last loaded records.
            </span>
          </p>
        ) : null}
        {result.patients.some((patient) =>
          ['SMS_NOT_READY', 'ROUTING_NOT_READY'].includes(
            patient.smsContactCapability?.reason ?? ''
          )
        ) ? (
          <section
            className={styles.smsSetupNotice}
            aria-label="Clinic text messaging"
            role="status"
          >
            <MessageSquareOff size={16} aria-hidden="true" />
            <div>
              <p>Text messaging isn't set up for this clinic yet.</p>
              {communicationsSetup ?? (
                <p>
                  An organization owner or admin can continue phone &amp;
                  texting setup.
                </p>
              )}
            </div>
          </section>
        ) : null}
        <PatientList
          status={result.status}
          patients={result.patients}
          totalPatients={result.totalPatients}
          startIndex={result.startIndex}
          hasPrevious={previousCursors.length > 0}
          hasNext={Boolean(result.nextCursor)}
          refreshing={result.refreshing}
          onPrevious={() => {
            const previous = previousCursors[previousCursors.length - 1] ?? null
            setPreviousCursors((current) => current.slice(0, -1))
            setCursor(previous)
          }}
          onNext={() => {
            if (!result.nextCursor) return
            setPreviousCursors((current) => [...current, cursor])
            setCursor(result.nextCursor)
          }}
          onSelect={(patient) => onOpenPatient?.(patient)}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          filter={filter}
          onFilterChange={(nextFilter) => {
            setFilter(nextFilter)
            setCursor(null)
            setPreviousCursors([])
          }}
          onAdd={() => setAddOpen(true)}
          onRetry={result.refresh}
          onClearFilters={() => {
            setSearchQuery('')
            setActiveQuery('')
            setFilter('all')
            setCursor(null)
            setPreviousCursors([])
          }}
          operationalStates={operational.states}
          onSendSms={
            contactDirectoryRepository
              ? (patient) => setSmsPatient(patient)
              : undefined
          }
        />
      </div>
      {addOpen ? (
        <AddPatientDialog
          tenantId={tenantId}
          repository={repository}
          serviceIntents={result.serviceIntents}
          onClose={() => setAddOpen(false)}
          onCreated={(patientAuthorityId, outcome) => {
            setAddOpen(false)
            result.refresh()
            onOpenPatientId?.(patientAuthorityId, outcome)
          }}
        />
      ) : null}
      {smsPatient && contactDirectoryRepository ? (
        <PatientSmsDialog
          key={`${tenantId}:${smsPatient.patientAuthorityId}`}
          tenantId={tenantId}
          patient={smsPatient}
          repository={contactDirectoryRepository}
          onClose={() => setSmsPatient(null)}
          onAccepted={() => {
            result.refresh()
            void operational.refresh()
          }}
        />
      ) : null}
    </div>
  )
}

export function PatientsPage({
  repository,
}: {
  repository?: PatientsRepository
}) {
  const auth = useAuth()
  const tenant = useTenant()
  const organization = useOrganizationAdmin()
  const navigate = useNavigate()
  const currentUser = auth.status === 'authenticated' ? auth.user : null
  const activeRepository = useMemo(
    () =>
      repository ?? createPatientsRepository({ getUser: () => currentUser }),
    [repository, currentUser]
  )
  const operationalRepository = useMemo(
    () =>
      createInteractionOperationalRepository({ getUser: () => currentUser }),
    [currentUser]
  )
  const contactDirectoryRepository = useMemo(
    () => createContactDirectoryRepository({ getUser: () => currentUser }),
    [currentUser]
  )
  const readSideRepository = useMemo(
    () => createReadSideRepository({ getUser: () => currentUser }),
    [currentUser]
  )
  const readSide = useReadSideStatus(
    readSideRepository,
    tenant.selectedClinic?.id ?? ''
  )
  if (!tenant.selectedClinic) return null
  return (
    <PatientsContent
      key={tenant.selectedClinic.id}
      tenantId={tenant.selectedClinic.id}
      repository={activeRepository}
      communicationsSetup={
        organization.data &&
        ['OWNER', 'ADMIN'].includes(organization.data.role) &&
        organization.data.clinics.some(
          (clinic) => clinic.tenantUid === tenant.selectedClinic!.id
        ) ? (
          <CommunicationsSetupStatus
            key={tenant.selectedClinic.id}
            tenantUid={tenant.selectedClinic.id}
          />
        ) : undefined
      }
      readSideStatus={readSide.data}
      operationalRepository={operationalRepository}
      contactDirectoryRepository={contactDirectoryRepository}
      onOpenPatient={(patient) =>
        void navigate(
          `/patients/${encodeURIComponent(patient.patientAuthorityId)}`
        )
      }
      onOpenPatientId={(patientAuthorityId, addOutcome) =>
        void navigate(
          `/patients/${encodeURIComponent(patientAuthorityId)}`,
          addOutcome ? { state: { patientAddOutcome: addOutcome } } : undefined
        )
      }
    />
  )
}
